import React, { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConsentManagerProvider, PostHogProviderWrapper } from "@entur-partner/app-shell";
import { Box, ErrorBoundary } from "@entur-partner/common";
import type { MicroFrontendPayload } from "@entur-partner/micro-frontend";
import { ToastProvider } from "@entur/alert";
import { Loader } from "@entur/loader";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { AppProvider } from "@app/AppProvider.tsx";
import { createAppRouter } from "@app/router.tsx";
import { ErrorMessage } from "@components/ErrorMessage.tsx";
import { initAuth } from "@/api/ops-api-client.ts";
import config from "@/config.ts";

interface AppProps extends MicroFrontendPayload {}

const queryClient = new QueryClient();

export function App(props: AppProps) {
  const { t } = useTranslation();
  const [router] = useState(() => createAppRouter(queryClient));
  initAuth(props.getToken);

  return (
    <React.StrictMode>
      <ErrorBoundary
        fallback={
          <Box>
            <ErrorMessage />
          </Box>
        }
      >
        <ConsentManagerProvider>
          <PostHogProviderWrapper
            postHogApiKey={config.postHogApiKey}
            isStandalone={import.meta.env.VITE_APP_STANDALONE === "true"}
          >
            <QueryClientProvider client={queryClient}>
              <Suspense fallback={<Loader>{t("common.loading")}</Loader>}>
                <AppProvider {...props}>
                  <ToastProvider>
                    <RouterProvider router={router} />
                  </ToastProvider>
                </AppProvider>
              </Suspense>
            </QueryClientProvider>
          </PostHogProviderWrapper>
        </ConsentManagerProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}
